import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import API_BASE_URL from './config';
import './css/ParticipantMaintenance.css';

const ParticipantMaintenance = () => {
  const [participants, setParticipants] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [selectedLeaderFilter, setSelectedLeaderFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    _id: '',
    email: '',
    relationship: '',
    customerId: '',
    leaderEmail: '',
    participantName: '',
    leaderProduct: '',
    completed: false,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductFilter, setSelectedProductFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [participantsRes, productsRes, customersRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/all-participants`, { withCredentials: true }),
          axios.get(`${API_BASE_URL}/api/products`).catch(error => {
            console.error('Products fetch failed:', error.response?.data || error.message);
            return { data: [] };
          }),
          axios.get(`${API_BASE_URL}/api/customers`, { withCredentials: true }),
        ]);
        setParticipants(participantsRes.data);
        setProducts(productsRes.data);
        setCustomers(customersRes.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Failed to load data. Check the console for details.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLeaderChange = (selectedOption) => {
    setSelectedLeader(selectedOption);
    setFormData({ ...formData, customerId: selectedOption ? selectedOption.value : '' });
  };

  const handleEditParticipant = (participant) => {
    const selectedCustomer = participant.customerId ? {
      value: participant.customerId._id,
      label: `${participant.customerId.firstName} ${participant.customerId.lastName}`
    } : null;
    setSelectedLeader(selectedCustomer);
    setFormData({
      _id: participant._id,
      participantName: participant.participantName,
      email: participant.email,
      leaderEmail: participant.customerId ? participant.customerId.email : '',
      customerId: participant.customerId ? participant.customerId._id : '',
      relationship: participant.relationship,
      leaderProduct: participant.customerId && participant.customerId.product ? participant.customerId.product.productName : '',
      completed: participant.completed || false,
    });
    setIsEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const submitData = { ...formData };
      if (!isEditMode) delete submitData._id;
      const response = await axios.post(`${API_BASE_URL}/api/participants`, submitData, { withCredentials: true });
      if (response.status === 200) {
        alert('Participant added/updated successfully.');
        fetchParticipants();
        resetForm();
      }
    } catch (error) {
      alert('Failed to add/update participant: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/all-participants`, { withCredentials: true });
      setParticipants(response.data);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const validateForm = () => {
    if (!formData.email) {
      alert('Email is required.');
      return false;
    }
    if (!formData.customerId) {
      alert('Leader selection is required.');
      return false;
    }
    if (!formData.leaderEmail) {
      alert('Leader Email is required.');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setFormData({
      _id: '',
      email: '',
      relationship: '',
      customerId: '',
      leaderEmail: '',
      participantName: '',
      leaderProduct: '',
      completed: false,
    });
    setSelectedLeader(null);
    setIsEditMode(false);
  };

  const handleSendInvitation = async (participantId) => {
    try {
      const adminName = '360 Admin';
      const response = await axios.post(
        `${API_BASE_URL}/api/send-feedback-form`,
        { participantId, adminName },
        { withCredentials: true }
      );
      if (response.status === 200) {
        alert('Feedback email sent successfully.');
        fetchParticipants();
      }
    } catch (error) {
      alert('Failed to send feedback email: ' + (error.response?.data?.message || error.message));
    }
  };

  // Updated Delete Handler
  const handleDeleteParticipant = async (participantId) => {
    if (window.confirm('Are you sure you want to delete this participant?')) {
      try {
        console.log('Attempting to delete participant with ID:', participantId);
        const response = await axios.delete(`${API_BASE_URL}/api/participants/${participantId}`, { withCredentials: true });
        if (response.status === 200) {
          alert('Participant deleted successfully.');
          setParticipants((prevParticipants) => {
            const updatedParticipants = prevParticipants.filter(participant => participant._id.toString() !== participantId);
            console.log('Updated participants:', updatedParticipants);
            return updatedParticipants;
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          alert('Participant not found in the database.');
        } else {
          alert('Failed to delete participant: ' + (error.response?.data?.message || error.message));
        }
      }
    }
  };

  const customerOptions = customers.map((customer) => ({
    value: customer._id,
    label: `${customer.firstName} ${customer.lastName}`,
  }));

  const productOptions = useMemo(() => [
    { value: '', label: 'All Products' },
    ...products.map((product) => ({ value: product.productName, label: product.productName })),
  ], [products]);

  const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const getSearchableFields = (participant) => {
    const fields = [
      participant.participantName,
      participant.email,
      participant.relationship,
      participant.completed ? 'Yes' : 'No',
    ];
    if (participant.customerId) {
      fields.push(
        `${participant.customerId.firstName} ${participant.customerId.lastName}`,
        participant.customerId.email,
        participant.customerId.product ? participant.customerId.product.productName : ''
      );
    }
    return fields.filter(Boolean).map(value => value.toString().toLowerCase());
  };

  const filteredParticipants = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return participants.filter((participant) => {
      const matchesSearch = getSearchableFields(participant).some(field => field.includes(searchLower));
      const matchesProduct = selectedProductFilter 
        ? participant.customerId && participant.customerId.product && participant.customerId.product.productName === selectedProductFilter 
        : true;
      const matchesLeader = selectedLeaderFilter 
        ? participant.customerId && participant.customerId._id.toString() === selectedLeaderFilter.value 
        : true;
      return matchesSearch && matchesProduct && matchesLeader;
    });
  }, [participants, searchTerm, selectedProductFilter, selectedLeaderFilter]);

  const totalParticipants = filteredParticipants.length;
  const totalPages = Math.ceil(totalParticipants / itemsPerPage);
  const paginatedParticipants = filteredParticipants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading Data...</p>
      </div>
    );
  }

  return (
    <div className="participant-maintenance-container">
      <h2>{isEditMode ? 'Edit Participant' : 'Add Participant'}</h2>
      <div className="participant-maintenance-form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="participantName">Participant Name</label>
            <input
              type="text"
              id="participantName"
              name="participantName"
              value={formData.participantName}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="relationship">Relationship</label>
            <select
              id="relationship"
              name="relationship"
              value={formData.relationship}
              onChange={handleInputChange}
            >
              <option value="">Select a relationship</option>
              {['Self', 'Direct Report', 'Other', 'Peer', 'Manager'].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="leaderName">Leader Name</label>
            <Select
              id="leaderName"
              options={customerOptions}
              value={selectedLeader}
              onChange={handleLeaderChange}
              placeholder="Search for a leader..."
              isSearchable
            />
          </div>
          <div className="form-group">
            <label htmlFor="leaderEmail">Leader Email</label>
            <input
              type="email"
              id="leaderEmail"
              name="leaderEmail"
              value={formData.leaderEmail}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="leaderProduct">Product</label>
            <select
              id="leaderProduct"
              name="leaderProduct"
              value={formData.leaderProduct}
              onChange={handleInputChange}
            >
              <option value="">Select a product</option>
              {products.map((product) => (
                <option key={product._id} value={product.productName}>
                  {product.productName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group full-submit">
            <input type="submit" value={isEditMode ? 'Update Participant' : 'Add Participant'} />
          </div>
        </form>
      </div>

      <h2>Participants</h2>
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="search">Search</label>
          <div className="search-bar-container">
            <input
              type="text"
              id="search"
              className="search-bar"
              placeholder="Search by name, email, or leader..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <div className="filter-group">
          <label htmlFor="productFilter">Product Filter</label>
          <Select
            id="productFilter"
            options={productOptions}
            value={productOptions.find((option) => option.value === selectedProductFilter)}
            onChange={(option) => {
              setSelectedProductFilter(option.value);
              setCurrentPage(1);
            }}
            placeholder="Select Product"
            className="product-filter-select"
            classNamePrefix="react-select"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="leaderFilter">Leader Filter</label>
          <Select
            id="leaderFilter"
            options={customerOptions}
            value={selectedLeaderFilter}
            onChange={(option) => {
              setSelectedLeaderFilter(option);
              setCurrentPage(1);
            }}
            placeholder="Select Leader"
            className="leader-filter-select"
            classNamePrefix="react-select"
            isClearable
          />
        </div>
        <div className="filter-group">
          <label htmlFor="itemsPerPage">Items per page</label>
          <Select
            id="itemsPerPage"
            options={itemsPerPageOptions}
            value={itemsPerPageOptions.find((option) => option.value === itemsPerPage)}
            onChange={(option) => {
              setItemsPerPage(option.value);
              setCurrentPage(1);
            }}
            className="items-per-page-select"
            classNamePrefix="react-select"
          />
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Relationship</th>
              <th>Leader Name</th>
              <th>Leader Email</th>
              <th>Product</th>
              <th>Completed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedParticipants.map((participant) => (
              <tr key={participant._id}>
                <td>{participant.participantName}</td>
                <td>{participant.email}</td>
                <td>{participant.relationship}</td>
                <td>{participant.customerId ? `${participant.customerId.firstName} ${participant.customerId.lastName}` : 'N/A'}</td>
                <td>{participant.customerId ? participant.customerId.email : 'N/A'}</td>
                <td>{participant.customerId && participant.customerId.product ? participant.customerId.product.productName : 'N/A'}</td>
                <td>{participant.completed ? 'Yes' : 'No'}</td>
                <td className="action-column">
                  <button onClick={() => handleEditParticipant(participant)}>
                    <span role="img" aria-label="edit">✏️</span> Edit
                  </button>
                  <button onClick={() => handleSendInvitation(participant._id)}>
                    <span role="img" aria-label="send">📧</span> Send
                  </button>
                  <button className="delete-button" onClick={() => handleDeleteParticipant(participant._id)}>
                    <span role="img" aria-label="delete">🗑️</span> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            disabled={currentPage === page}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ParticipantMaintenance;